import { v4 as uuid } from 'uuid';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';

import type { ReferralCreateData, ReferralData, ReferralUpdateForm } from 'chaika-frontend-types/frontend-cms';
import { ReferralStatus, EntryTypeFieldName } from 'chaika-enums';

import { store } from '@store';
import { MedicalCaseApi } from '@api/MedicalCaseApi';
import { ReferralApi } from '@api/ReferralApi';
import { serviceService } from '@services/services/serviceService';
import { GroupedListParams } from '@store/abstract/GroupedList';
import { ReferralList } from '@store/models/SystemFields/Referral';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { ehrsServicePlansService } from '@patient/services/ehr/ehrsServicePlansService';
import { priceListService } from '@modules/price/service/priceList/priceListService';
import { expandCompositeReferrals } from '@store/services/utils/expandCompositeReferrals';
import { translate } from '@chaika/i18n/helper';

interface AdditionalCreateParams {
    ehrId: string;
    planActualOnDate?: string;
    entryId?: string;
}

interface RemoveParams {
    ehrId: string;
    entryId?: string;
}

interface UpdateReferralParams {
    ehrId: string;
    groupReferralId: string;
    params: ReferralUpdateForm;
    entryId?: string;
    withDuration?: boolean;
}

export abstract class ReferralAbstractService {
    public abstract add(
        id: string,
        addedCount: number,
        medicalCaseId: string | string[],
        referringDoctorId: string,
    ): Promise<void>;
    public abstract update(id: string, params: ReferralUpdateForm): Promise<void>;
    public abstract remove(id: string, count?: number): Promise<void>;

    public async attachReferral(
        medicalCaseId: string | string[],
        referralGroupId: string,
        referringDoctorId: string,
    ): Promise<void> {
        const referral = this.getReferral(referralGroupId);
        const { id: referralId, ehrId, parentEntryId } = referral;

        if (isArray(medicalCaseId)) {
            await MedicalCaseApi.addReferralToMedicalCases(referralId, {
                medicalCaseIds: medicalCaseId,
                entryId: parentEntryId,
            });
        } else {
            await MedicalCaseApi.addReferral(medicalCaseId, { referralId, entryId: parentEntryId });
        }
        if (!(referral.coAuthorIds || []).includes(referringDoctorId)) {
            await ReferralApi.addCoAuthor(ehrId, [referralId], referringDoctorId);
            referral.coAuthorIds.push(referringDoctorId);
        }
    }

    public async dettachReferral(
        medicalCaseId: string | string[],
        referralGroupId: string,
        referringDoctorId: string,
    ): Promise<void> {
        const referralList = store.entry.fields.getValue<ReferralList>(EntryTypeFieldName.Referrals);
        const referral = referralList.getExisting(referralGroupId);

        referral.coAuthorIds = referral.coAuthorIds.filter(id => id !== referringDoctorId);

        await Promise.all([
            isArray(medicalCaseId)
                ? MedicalCaseApi.removeReferralFromMedicalCases(referral.id, {
                      medicalCaseIds: medicalCaseId,
                      entryId: store.entry.id,
                  })
                : MedicalCaseApi.removeReferral(medicalCaseId, referral.id, store.entry.id),
            ReferralApi.removeCoAuthor(referral.ehrId, [referral.id], referringDoctorId, store.entry.id),
        ]);
    }

    public getReferralsByIds(referralIds: string[]): GroupedListParams<ReferralData>[] {
        const referralList = this.getReferralList();

        return referralList.getFilteredValues({ ids: referralIds });
    }

    protected abstract getReferral(id: string): GroupedListParams<ReferralData>;
    protected abstract getReferralList(): ReferralList;

    protected async createReferral(
        referrals: ReferralCreateData[],
        { ehrId, entryId, planActualOnDate }: AdditionalCreateParams,
        createList?: boolean,
    ): Promise<ReferralData[]> {
        const {
            authUser: { id: authorId },
            priceListStore,
        } = store;

        const creationTime = new Date().toISOString();

        await priceListService.loadCompositeServices();

        const createParams = expandCompositeReferrals(
            referrals.map(referral => ({
                entryId: !!entryId && entryId === '' ? entryId : undefined,
                ...referral,
            })),
            serviceId => priceListStore.getCompositeServiceIdsByServiceId(serviceId),
        );
        const servicesIds = createParams.map(params => params.serviceId);
        const isSplitIntoGroups = createList || createParams.length > referrals.length;

        await serviceService.loadByIds(servicesIds);

        await Promise.all<any>([
            isSplitIntoGroups ? ReferralApi.createList(ehrId, createParams) : ReferralApi.create(ehrId, createParams),
            !!planActualOnDate &&
                ehrsServicePlansService.loadServicePlans({
                    ehrId,
                    serviceIds: servicesIds,
                    actualOn: planActualOnDate,
                }),
        ]);

        return createParams.map(({ medicalCaseIds, ...params }) => ({
            ...params,
            authorId,
            creationTime,
            status: ReferralStatus.Active,
        })) as any as ReferralData[];
    }

    protected async updateReferral({
        ehrId,
        groupReferralId,
        params,
        entryId,
        withDuration,
    }: UpdateReferralParams): Promise<void> {
        const referral = this.getReferral(groupReferralId);
        const duration = withDuration && params.duration === 0 ? undefined : params.duration;
        const omitedParams = omit({ ...params, duration }, ['amountToPay', 'totalCost', 'currency', 'medicalCaseIds']);

        const addedMedicalCaseIds = params?.medicalCaseIds?.filter(id => !referral.medicalCaseIds.includes(id)) ?? [];
        const removedMedicalCaseIds = params?.medicalCaseIds
            ? referral.medicalCaseIds.filter(id => !params.medicalCaseIds?.includes(id))
            : [];

        await Promise.all([
            !isEmpty(omitedParams) && ReferralApi.update(ehrId, { entryId, ids: referral.ids.join(',') }, omitedParams),
            addedMedicalCaseIds.length &&
                MedicalCaseApi.addReferralToMedicalCases(referral.id, {
                    entryId,
                    medicalCaseIds: addedMedicalCaseIds,
                }),
        ]);

        // Only remove cases after adding to prevent accidental removing of all cases.
        if (removedMedicalCaseIds.length) {
            await MedicalCaseApi.removeReferralFromMedicalCases(referral.id, {
                entryId,
                medicalCaseIds: removedMedicalCaseIds,
            });
        }
    }

    protected async addReferrals(
        id: string,
        addedCount: number,
        ehrId: string,
        medicalCaseId: string | string[],
    ): Promise<string[]> {
        const {
            id: referralId,
            serviceId,
            parentEntryId,
            duration,
            comment,
            userId,
            organizationId,
            startDate,
            service,
            compositeService,
            contractorId,
        } = this.getReferral(id);

        const newIds: string[] = [];

        for (let i = 0; i < addedCount; i++) {
            newIds.push(uuid());
        }

        const { laboratoryService, ...serviceParams } = service || {};
        const { id: labId, ...labParams } = laboratoryService || {};
        const labReferral = store.serviceStore.isServiceLaboratory(serviceId);

        const laboratories = store.laboratoryStore.getLaboratoryServicesByServiceId(serviceId) || [];
        // @ts-ignore
        const { laboratoryId, labCode, bioCode } =
            laboratories.find(({ laboratoryId }) => laboratoryId === labId) || laboratories[0] || {};

        const referralMedicalCaseIds = store.ehr.medicalCases.getFilteredValuesIds({ referralId });

        const createParams = newIds.map(id => ({
            id,
            serviceId,
            duration,
            comment,
            userId,
            startDate,
            organizationId,
            contractorId,
            coAuthorIds: [],
            entryId: parentEntryId,
            medicalCaseIds: uniq([
                ...(isArray(medicalCaseId) ? medicalCaseId : [medicalCaseId]),
                ...referralMedicalCaseIds,
            ]),
            serviceParams: !!service
                ? {
                      ...serviceParams,
                      laboratoryService: labReferral
                          ? {
                                ...labParams,
                                // @ts-ignore
                                laboratoryId: labParams?.laboratoryId ?? laboratoryId,
                                // @ts-ignore
                                labCode: labParams?.labCode ?? labCode,
                                // @ts-ignore
                                bioCode: labParams?.bioCode ?? bioCode,
                            }
                          : undefined,
                  }
                : undefined,
            compositeServices: compositeService?.compositeServices as any,
        }));

        await ReferralApi.create(ehrId, createParams);

        return newIds;
    }

    protected async removeReferrals({ ehrId, entryId }: RemoveParams, id: string, removingCount?: number) {
        const { ids } = this.getReferral(id);
        let removingIds = removingCount ? ids.slice(0, removingCount) : ids;

        try {
            const chunked = getUniqChunkedIds(removingIds);
            await Promise.all(
                chunked.map(ids =>
                    ReferralApi.updateStatus(ehrId, { entryId, ids: ids.join(',') }, ReferralStatus.Cancelled),
                ),
            );
        } catch (e: any) {
            removingIds = [];
            store.app.showingPopupParams = {
                title: translate('failReferralDelete'),
                description: `${translate('error')}: ${e.message}`,
            };
        }

        return removingIds;
    }

    protected removeFromList(id: string, removingIds: string[] = []): void {
        if (removingIds.length > 0) {
            this.getReferralList().removeItems(id, removingIds);
        }
    }
}
